import type { LicenseTypeConfigurationResponseSchema } from "@/hera-api/client/types.gen";
import { formatCurrency } from "@/lib/utils";
import type { ReactNode } from "react";

interface FeeInformationProps {
  configurationData?: LicenseTypeConfigurationResponseSchema;
  isRenewal?: boolean;
}

export function FeeInformation({
  configurationData,
  isRenewal = false,
}: FeeInformationProps) {
  if (!configurationData) {
    return null;
  }

  const registrationFee = Number(
    configurationData?.registration_fee ?? 0,
  ).toFixed(2);
  const registrationFee2 =
    configurationData?.registration_fee_2 != null
      ? Number(configurationData.registration_fee_2).toFixed(2)
      : undefined;
  const registrationFee3 =
    configurationData?.registration_fee_3 != null
      ? Number(configurationData.registration_fee_3).toFixed(2)
      : undefined;
  const registrationFee4 =
    configurationData?.registration_fee_4 != null
      ? Number(configurationData.registration_fee_4).toFixed(2)
      : undefined;
  const registrationFee5 =
    configurationData?.registration_fee_5 != null
      ? Number(configurationData.registration_fee_5).toFixed(2)
      : undefined;

  const firstLateFee = configurationData?.first_late_fee_percentage
    ? `${configurationData.first_late_fee_percentage}% of registration fee`
    : configurationData?.first_late_fee_amount != null
      ? Number(configurationData.first_late_fee_amount).toFixed(2)
      : "0.00";
  const recurringLateFee = configurationData?.recurring_late_fee_percentage
    ? `${configurationData.recurring_late_fee_percentage}% of registration fee`
    : configurationData?.recurring_late_fee_amount != null
      ? Number(configurationData.recurring_late_fee_amount).toFixed(2)
      : "0.00";
  const lateFeeCap =
    configurationData?.late_fee_cap != null
      ? Number(configurationData.late_fee_cap).toFixed(2)
      : null;

  // Determine if the fee structure is based on property type
  const isStructureFee = configurationData?.fee_structure === "structure";
  const isCommercialManagedPropertyReduction =
    configurationData?.fee_structure ===
    "commercial_managed_property_reduction";
  const isUnitsFee = configurationData?.fee_structure === "based_on_units";
  const isPeriodAndVacancyFee =
    configurationData?.fee_structure === "based_on_period_and_vacancy";

  /** Format dollar amounts with 2 decimal places (e.g. $75.00) */
  const fmtCurrency = (amount: string | number | null | undefined) =>
    formatCurrency(amount);

  // Build a property type fee summary if structure-based
  let propertyTypeFeeSummary: string | undefined;
  if (isStructureFee) {
    const propertyTypes = [
      { label: "Residential", value: configurationData?.registration_fee },
      { label: "Multi-Family", value: configurationData?.registration_fee_2 },
      { label: "Vacant Lot", value: configurationData?.registration_fee_3 },
      { label: "Mobile Home", value: configurationData?.registration_fee_4 },
      { label: "Commercial", value: configurationData?.registration_fee_5 },
      { label: "Industrial", value: configurationData?.registration_fee_6 },
    ];
    propertyTypeFeeSummary = propertyTypes
      .filter((pt) => pt.value != null)
      .map((pt) => `${pt.label}: ${fmtCurrency(Number(pt.value).toFixed(2))}`)
      .join(", ");
  }

  // Build period & vacancy fee summary
  let periodAndVacancySummary: string | undefined;
  if (isPeriodAndVacancyFee) {
    const occupiedFees = [
      { label: "Occupied 1st", value: configurationData?.registration_fee },
      { label: "Occupied 2nd", value: configurationData?.registration_fee_2 },
      { label: "Occupied 3rd", value: configurationData?.registration_fee_3 },
      { label: "Occupied 4th", value: configurationData?.registration_fee_4 },
      { label: "Occupied 5th", value: configurationData?.registration_fee_5 },
      { label: "Occupied 6th", value: configurationData?.registration_fee_6 },
    ];
    const vacantFees = [
      { label: "Vacant 1st", value: configurationData?.registration_fee_7 },
      { label: "Vacant 2nd", value: configurationData?.registration_fee_8 },
      { label: "Vacant 3rd", value: configurationData?.registration_fee_9 },
      { label: "Vacant 4th", value: configurationData?.registration_fee_10 },
      { label: "Vacant 5th", value: configurationData?.registration_fee_11 },
      { label: "Vacant 6th", value: configurationData?.registration_fee_12 },
    ];
    const allFees = [...occupiedFees, ...vacantFees];
    periodAndVacancySummary = allFees
      .filter((f) => f.value != null)
      .map((f) => `${f.label}: ${fmtCurrency(Number(f.value).toFixed(2))}`)
      .join(", ");
  }

  // Build commercial managed property reduction fee summary
  let commercialManagedPropertySummary: string | undefined;
  if (isCommercialManagedPropertyReduction) {
    const registrationFee = configurationData?.registration_fee;
    const managedCommercialPropertyFee = configurationData?.registration_fee_12;
    const fees = [] as string[];
    if (registrationFee) {
      fees.push(
        `Registration Fee: ${fmtCurrency(Number(registrationFee).toFixed(2))}`,
      );
    }
    if (managedCommercialPropertyFee) {
      fees.push(
        `Managed Commercial Property: ${fmtCurrency(Number(managedCommercialPropertyFee).toFixed(2))}`,
      );
    }
    commercialManagedPropertySummary = fees.join(", ");
  }

  const perUnitFee = registrationFee;

  // Inspection fee (only when structure is set and not "none")
  const inspectionFeeStructure = configurationData?.inspection_fee_structure;
  const hasInspectionFee =
    inspectionFeeStructure &&
    inspectionFeeStructure !== "none" &&
    (configurationData?.inspection_fee != null ||
      configurationData?.inspection_fee_2 != null);
  const isInspectionFeeStructure = inspectionFeeStructure === "structure";
  const isInspectionFeeUnits = inspectionFeeStructure === "based_on_units";
  let inspectionFeeSummary: ReactNode = null;
  if (hasInspectionFee) {
    const fmt = (v: string | number | null | undefined) =>
      v != null && String(v).trim() !== ""
        ? fmtCurrency(Number(v).toFixed(2))
        : null;
    if (isInspectionFeeStructure) {
      const propertyTypes = [
        { label: "Residential", value: configurationData?.inspection_fee },
        { label: "Multi-Family", value: configurationData?.inspection_fee_2 },
        { label: "Vacant Lot", value: configurationData?.inspection_fee_3 },
        { label: "Mobile Home", value: configurationData?.inspection_fee_4 },
        { label: "Commercial", value: configurationData?.inspection_fee_5 },
        { label: "Industrial", value: configurationData?.inspection_fee_6 },
      ];
      const parts = propertyTypes
        .filter((pt) => pt.value != null && String(pt.value).trim() !== "")
        .map((pt) => `${pt.label}: ${fmt(pt.value)}`);
      inspectionFeeSummary =
        parts.length > 0 ? (
          <>
            <b>Inspection fee:</b> varies by property type — {parts.join(", ")}{" "}
            (per inspection).
          </>
        ) : null;
    } else if (
      isInspectionFeeUnits &&
      configurationData?.inspection_fee != null
    ) {
      inspectionFeeSummary = (
        <>
          <b>
            Inspection fee: {fmt(configurationData.inspection_fee)} per unit
          </b>{" "}
          (per inspection).
        </>
      );
    } else if (
      configurationData?.inspection_fee != null &&
      String(configurationData.inspection_fee).trim() !== ""
    ) {
      inspectionFeeSummary = (
        <>
          <b>
            Inspection fee: {fmt(configurationData.inspection_fee)} per unit
          </b>{" "}
          (per inspection).
        </>
      );
    }
  }

  return (
    <div className="space-y-2 p-4 border rounded-md bg-yellow-50">
      <h3 className="font-medium text-sm text-yellow-800">Fee Information</h3>
      <div className="space-y-1">
        <p className="text-sm text-yellow-800">
          {isStructureFee ? (
            <>
              <b>Registration fee:</b> varies by property type —{" "}
              {propertyTypeFeeSummary} (per period).
            </>
          ) : isPeriodAndVacancyFee ? (
            <>
              <b>Registration fee:</b> varies by period and vacancy status —{" "}
              {periodAndVacancySummary} (last filled period repeats).
            </>
          ) : isUnitsFee ? (
            configurationData?.renewal_type === "one_time" ? (
              <>
                <b>Registration fee: {fmtCurrency(perUnitFee)} per unit</b>{" "}
                (one-time, charged at initial registration).
              </>
            ) : (
              <>
                <b>Registration fee: {fmtCurrency(perUnitFee)} per unit</b> (per
                renewal).
              </>
            )
          ) : isCommercialManagedPropertyReduction ? (
            <>
              <b>Registration fee:</b> {commercialManagedPropertySummary}.
            </>
          ) : (
            <>
              <b>Registration fee: {fmtCurrency(registrationFee)}</b> (per{" "}
              {isRenewal ? "renewal" : "registration"} period).
              {registrationFee2 && Number(registrationFee2) !== 0 && (
                <> 2nd: {fmtCurrency(registrationFee2)} </>
              )}
              {registrationFee3 && Number(registrationFee3) !== 0 && (
                <> 3rd: {fmtCurrency(registrationFee3)} </>
              )}
              {registrationFee4 && Number(registrationFee4) !== 0 && (
                <> 4th: {fmtCurrency(registrationFee4)} </>
              )}
              {registrationFee5 && Number(registrationFee5) !== 0 && (
                <> 5th: {fmtCurrency(registrationFee5)} </>
              )}
            </>
          )}
        </p>
        {configurationData?.first_late_fee_enabled && (
          <p className="text-sm text-yellow-800">
            <b>First late fee: </b>
            {configurationData?.first_late_fee_percentage
              ? firstLateFee
              : fmtCurrency(firstLateFee)}{" "}
            (applies after {configurationData.first_late_fee_days} days).
          </p>
        )}
        {configurationData?.recurring_late_fee_enabled && (
          <p className="text-sm text-yellow-800">
            <b>Recurring late fee: </b>
            {configurationData?.recurring_late_fee_percentage
              ? recurringLateFee
              : fmtCurrency(recurringLateFee)}{" "}
            (applies after {configurationData.recurring_late_fee_days} days).
          </p>
        )}
        {lateFeeCap && (
          <p className="text-sm text-yellow-800">
            <b>Late fee cap: </b>
            {fmtCurrency(lateFeeCap)} (maximum total late fees per period).
          </p>
        )}
        {hasInspectionFee && inspectionFeeSummary && (
          <p className="text-sm text-yellow-800">{inspectionFeeSummary}</p>
        )}
      </div>
    </div>
  );
}
